// WSJF calculator with uncertainty: React UI on the BoldPanelLayout template. The maths
// lives in ./model.js; the page holds the jobs and projects analyseWsjf().

import { useMemo, useState } from "react";
import { BoldPanelLayout } from "@/components/templates";
import {
  BarFooter,
  BriefLines,
  Callout,
  DataTable,
  ExampleDataPicker,
  Explainer,
  Field,
  MetricCard,
  MetricGrid,
  Panel,
  RangeInput,
  UrlStateNotices,
  useUrlRestore,
  useUrlSync,
  type Range,
} from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { headingFor, ledeOf } from "@/lib/pageHeadings";
import { PARTS, PART_LABELS, analyseWsjf, fmt, pct, summaryLines } from "./model.js";
import { EXAMPLES, exampleById } from "./examples.js";
import { wsjfShare } from "./share.js";

const HEADING = headingFor("tool/wsjf-calculator");

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

interface Job {
  label: string;
  value: Range;
  time: Range;
  risk: Range;
  size: Range;
}
interface State {
  top: number;
  jobs: Job[];
}

const INITIAL = EXAMPLES[0].state as State;

export function App() {
  const shared = useUrlRestore<State>(wsjfShare, (s) => setState(s));
  const [state, setState] = useState<State>(shared.initial ?? INITIAL);
  useUrlSync(wsjfShare, () => state, [state], shared);
  const r: Any = useMemo(() => analyseWsjf(state), [state]);
  const setJob = (i: number, patch: Partial<Job>) => setState((s) => ({ ...s, jobs: s.jobs.map((j, k) => (k === i ? { ...j, ...patch } : j)) }));
  const first = r.empty ? null : r.jobs[0];

  return (
    <BoldPanelLayout
      label="Unit 37 / WSJF"
      tags={<span className="hidden md:inline">Local · nothing leaves the page</span>}
      eyebrow="WSJF calculator"
      title={HEADING.h1}
      lede={ledeOf(HEADING)}
      footer={
        <BarFooter>
          <span>Cost of delay ÷ job size · scores as ranges</span>
          <span>{first ? `${first.label} first in ${pct(first.pFirst)} of scorings` : "Add a job"}</span>
        </BarFooter>
      }
    >
      <Explainer id="wsjf" title="What this answers" summary="Which job first, and how sure that order is" defaultOpen>
        <p>
          Weighted shortest job first puts the job with the highest <strong>cost of delay per unit of size</strong>{" "}
          first. Cost of delay is the sum of three relative scores: business value, time criticality, and risk
          reduction or opportunity enablement.
        </p>
        <p>
          A scoring session usually writes one number per score, and the order looks certain. Here each score is a
          range — what the room would actually agree to. The page scores the jobs thousands of times within those
          ranges and counts how often each job lands in each place.
        </p>
      </Explainer>

      <Panel step={1} title="Start from an example" hint="Or score your own jobs in 02">
        <UrlStateNotices state={shared} />
        <ExampleDataPicker
          examples={EXAMPLES.map(({ id, name, description }) => ({ id, name, description }))}
          onLoad={(id) => {
            const ex = exampleById(id);
            if (ex) setState(ex.state as State);
          }}
          deepLink={!shared.initial}
        />
        <p className="mb-0 mt-3 text-xs text-muted-foreground">Examples are illustrative: invented jobs and scores.</p>
      </Panel>

      <Panel step={2} title="Jobs and scores" hint="Relative scores · equal ends if sure">
        <DataTable<Job>
          rows={state.jobs}
          rowKey={(_, i) => i}
          rowClassName={(_, i) => (r.excluded.some((e: Any) => e.index === i) ? "bg-destructive/10" : undefined)}
          columns={[
            {
              head: "Job",
              cell: (j, i) => <Input aria-label={`Job ${i + 1} name`} className="h-8 min-w-[10rem] rounded-none" value={j.label} onChange={(e) => setJob(i, { label: e.target.value })} />,
            },
            ...PARTS.map((k: string) => ({
              head: (PART_LABELS as Record<string, string>)[k],
              cell: (j: Job, i: number) => (
                <RangeInput label={`${j.label || `Job ${i + 1}`} ${(PART_LABELS as Record<string, string>)[k].toLowerCase()}`} min={0} value={(j as Any)[k]} onChange={(v) => setJob(i, { [k]: v } as Partial<Job>)} />
              ),
            })),
            {
              head: "Job size",
              cell: (j: Job, i: number) => <RangeInput label={`${j.label || `Job ${i + 1}`} size`} min={0} value={j.size} onChange={(v) => setJob(i, { size: v })} />,
            },
            {
              head: <span className="sr-only">Remove</span>,
              cell: (j: Job, i: number) => (
                <Button variant="outline" size="sm" aria-label={`Remove ${j.label || `job ${i + 1}`}`} disabled={state.jobs.length <= 1} onClick={() => setState((s) => ({ ...s, jobs: s.jobs.filter((_, k) => k !== i) }))}>
                  ×
                </Button>
              ),
            },
          ]}
        />
        <Button
          className="mt-3"
          onClick={() =>
            setState((s) => ({
              ...s,
              jobs: [...s.jobs, { label: `Job ${s.jobs.length + 1}`, value: { low: 3, high: 8 }, time: { low: 1, high: 3 }, risk: { low: 1, high: 3 }, size: { low: 3, high: 8 } }],
            }))
          }
        >
          + Add job
        </Button>
        {r.excluded.length > 0 && (
          <Callout kind="warn" className="mt-4">
            <strong>Not ranked:</strong>
            <ul className="mb-0 mt-1 pl-5">
              {r.excluded.map((e: Any) => (
                <li key={e.index}>
                  {e.label || `Job ${e.index + 1}`}: {e.reasons.join(" ")}
                </li>
              ))}
            </ul>
          </Callout>
        )}
      </Panel>

      <Panel step={3} title="Order" hint={r.empty ? "Add a usable job" : "Conditional on the score ranges in 02"}>
        {r.empty ? (
          <Callout kind="error">There is no usable job to rank.</Callout>
        ) : (
          <>
            <MetricGrid className="mb-6 lg:grid-cols-3">
              <MetricCard accent eyebrow="Comes first" value={first.label} data-value={first.pFirst}>
                In {pct(first.pFirst)} of the scorings the ranges allow.
              </MetricCard>
              <MetricCard eyebrow="Close calls" value={String(r.jobs.filter((j: Any) => j.coinFlip).length)}>
                Neighbours in the order where the higher one is ahead less than 60% of the time.
              </MetricCard>
              <MetricCard eyebrow="Single-number order" value={r.sameAsPoint ? "Same" : "Differs"}>
                {r.sameAsPoint ? "Scoring the middle of each range gives the same order." : "Scoring the middle of each range gives a different order."}
              </MetricCard>
            </MetricGrid>
            <Field label="Top group size" className="mb-3 max-w-xs">
              <Input
                type="number"
                min={1}
                max={state.jobs.length}
                aria-label="Top group size"
                className="h-8 w-20 rounded-none font-mono text-xs"
                value={String(state.top)}
                onChange={(e) => setState((s) => ({ ...s, top: Math.max(1, Math.round(Number(e.target.value) || 1)) }))}
              />
            </Field>
            <DataTable<Any>
              caption="Ranked by expected WSJF"
              rows={r.jobs}
              rowKey={(j) => j.index}
              columns={[
                { head: "Place", numeric: true, cell: (j) => String(j.place).padStart(2, "0") },
                { head: "Job", cell: (j) => j.label },
                { head: "Expected WSJF", numeric: true, cell: (j) => fmt(j.expected) },
                { head: "Holds this place", numeric: true, cell: (j) => pct(j.pPlace) },
                { head: `In the top ${r.top}`, numeric: true, cell: (j) => pct(j.pTop) },
                {
                  head: "Ahead of the next",
                  numeric: true,
                  cell: (j) => (j.aheadOfNext == null ? "—" : <span className={j.coinFlip ? "font-bold text-destructive" : undefined}>{pct(j.aheadOfNext)}{j.coinFlip ? " · close call" : ""}</span>),
                },
              ]}
            />
            <DataTable<Any>
              className="mt-6"
              caption="Chance of each place"
              rows={r.jobs}
              rowKey={(j) => j.index}
              columns={[
                { head: "Job", cell: (j) => j.label },
                ...r.jobs.map((_: Any, k: number) => ({
                  head: `Place ${k + 1}`,
                  numeric: true,
                  cell: (j: Any) => pct(j.ranks[k]),
                })),
                { head: "Single-number WSJF", numeric: true, cell: (j) => fmt(j.point) },
              ]}
            />
            <BriefLines className="mt-6" lines={summaryLines(r)} />
          </>
        )}
      </Panel>
    </BoldPanelLayout>
  );
}
